import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { TableColumn, TableRowSelectorDirective, LegacyTableModule } from 'remarketing-ui-components-lib';
import { Store } from '@ngxs/store';
import { Organization, StatesProvincesService, LaunchDarklyService, AmbassadorService, AmbassadorAuthRequest, AvailableAmbassador} from 'remarketing-angular-library';

import { AnalyticsService, SaComponent } from 'smartauction-frontend-micro-library';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { AuthorizeAmbassadorsModalComponent } from '../authorize-ambassador-modal/authorize-ambassadors-modal.component';
import { AmbassadorResponseModalComponent, ModalVariant } from '../ambassador-response-modal/ambassador-response-modal.component';
import { closeDrawer, toggleDrawer } from '../../utils/drawer.utils';


@Component({
    selector: 'app-available-ambassadors',
    templateUrl: './available-ambassadors.component.html',
    styleUrls: ['./available-ambassadors.component.scss'],
    imports: [TranslocoDirective, LegacyTableModule, AuthorizeAmbassadorsModalComponent, AmbassadorResponseModalComponent]
})
export class AvailableAmbassadorsComponent extends SaComponent implements OnInit, AfterViewInit, OnDestroy {
  private countryCode: any;
  private organization: Organization;

  @ViewChild(AmbassadorResponseModalComponent) ambassadorResponseModalComponent: AmbassadorResponseModalComponent;
  @ViewChild('stateSelect') stateSelect: any;
  @ViewChild('provinceSelect') provinceSelect: any;
  @ViewChild(AuthorizeAmbassadorsModalComponent) authorizeModal: AuthorizeAmbassadorsModalComponent;
  @ViewChildren(TableRowSelectorDirective) drawers: QueryList<TableRowSelectorDirective>;

  textSelect: string;

  stateOrProvince: any;

  availableAmbassadorsTranslation = {
    textNoAmbassadors: '',
    textState: '',
    textProvince: '',
  };

  public columns: TableColumn[] = [];

  public cardConfig = {
    options: {
      fullWidth: true,
      showLabel: false
    }
  };

  public ambassadors: Array<AvailableAmbassador> = [];

  get isCanadianTenantEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  setStateOrProvince() {
    const list= [{
      name: this.textSelect,
      abbr: '--'
    }];
    this.stateOrProvince = this.isCanadianTenantEnabled ? list.concat(this.statesProvincesService.getCAProvinces()) : list.concat(this.statesProvincesService.getUSStates());
  }

  get stateOrProvinceLabel() {
    return this.isCanadianTenantEnabled ? this.availableAmbassadorsTranslation.textProvince : this.availableAmbassadorsTranslation.textState;
  }

  get stateCode() {
    const urlTree = this.router.parseUrl(this.router.url);
    const state = urlTree.queryParams.state;

    return state || this.organization.physicalAddress.state;
  }

  constructor(
    private readonly ambassadorService: AmbassadorService,
    private readonly store: Store,
    private readonly translocoService: TranslocoService,
    private readonly router: Router,
    private readonly analyticsService: AnalyticsService,
    private readonly statesProvincesService: StatesProvincesService,
    private readonly _launchDarklyService: LaunchDarklyService
  ) {
    super();
  }

  ngOnInit(): void {
    this.getTranslations();
    this.countryCode = this.isCanadianTenantEnabled ? 'CA' : 'US';
    this.setStateOrProvince();
  }

  ngAfterViewInit(): void {
    this.store
      .select(state => state.org)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(org => {
        this.organization = org.organization;
        this.selectStateIndex();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  selectStateIndex(): void {
    this.route(this.stateCode);
    this.getAvailableAmbassadorsForState(this.stateCode);
    const stateIndex = this.stateOrProvince.findIndex(s => s.abbr === this.stateCode);
    if (stateIndex >= 0) {
      if(this.isCanadianTenantEnabled){
        this.provinceSelect.nativeElement.selectedIndex = stateIndex;
      }
      else{
        this.stateSelect.nativeElement.selectedIndex = stateIndex;
      }
    }
  }

  public route(state: string): void {
    const navigationExtras = {
      queryParams: { state: state }
    } as NavigationExtras;
    this.router.navigate(['/ambassador-authorization/available'], navigationExtras);
  }

  getAvailableAmbassadorsForState(stateCode: string): void {
    this.ambassadorService
      .getAmbassadorsForCountryAndState(this.countryCode, stateCode)
      .pipe(take(1))
      .subscribe((response: any) => {
        if (response && response.ambassadors) {
          this.ambassadors = response.ambassadors;
        }
      });
  }

  stateChanged(): void {
    if (this.stateSelect.nativeElement.selectedIndex > 0) {
      this.analyticsService.track('clickEvent', {
        tagName: 'BUTTON',
        allytmln: 'stateSelected',
        linkName: 'stateSelected',
        href: 'javascript:void(0);'
      });

      this.ambassadors = [];
      const stateCode = this.stateOrProvince[this.stateSelect.nativeElement.selectedIndex].abbr;
      this.route(stateCode);
      this.getAvailableAmbassadorsForState(stateCode);
    }
  }

  provinceChanged(): void {
    if (this.provinceSelect.nativeElement.selectedIndex > 0) {
      this.analyticsService.track('clickEvent', {
        tagName: 'BUTTON',
        allytmln: 'provinceSelected',
        linkName: 'provinceSelected',
        href: 'javascript:void(0);'
      });

      this.ambassadors = [];
      const provinceCode = this.stateOrProvince[this.provinceSelect.nativeElement.selectedIndex].abbr;
      this.route(provinceCode);
      this.getAvailableAmbassadorsForState(provinceCode);
    }
  }

  toggleDrawer(index: number): void {
    toggleDrawer(this.drawers, index, this.analyticsService);
  }

  closeDrawer(index: number): void {
    closeDrawer(this.drawers, index);
  }

  openModal(ambassador: AvailableAmbassador) {
    const ambassadorAuthRequest: AmbassadorAuthRequest = {
      countryCode: this.countryCode,
      stateCode: this.stateCode,
      userUuid: ambassador.uuid
    };
    this.authorizeModal.show(ambassadorAuthRequest, ambassador);
  }

  setNewAvailableAmbassadors(ambassadors: Array<AvailableAmbassador>) {
    this.ambassadors = ambassadors;
  }

  showSuccess(name: string) {
    this.ambassadorResponseModalComponent.show(name, ModalVariant.AUTHORIZED);
  }

  reformatPhone(phone: string): string {
    if (phone) {
      phone = phone.replace(/-/g, '');
      if (phone.length > 0) {
        if (phone.length === 11) {
          phone = `(${phone.slice(1, 4)}) ${phone.slice(4, 7)}-${phone.slice(7)}`;
          return phone;
        } else {
          phone = `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
          return phone;
        }
      }
    }

    return undefined;
  }

  getTranslations(): void {
    const keyParams = {
      'ambassadorAuthorization': {},
      'common.select': {}
    };
    this.translocoService.selectTranslateObject(keyParams).pipe(takeUntil(this.destroyed$)).subscribe(translate => {
      this.columns = [
        { key: 'name',  label: translate[0].tableColumns.name },
        { key: 'phone',  label: translate[0].tableColumns.phone },
        { key: 'email',  label: translate[0].tableColumns.email },
        { key: 'actions',  label: translate[0].tableColumns.actions }
      ];
      this.textSelect = translate[1];
      this.availableAmbassadorsTranslation.textNoAmbassadors = translate[0].labels.textNoAmbassadors;
      this.availableAmbassadorsTranslation.textState = translate[0].labels.textState;
      this.availableAmbassadorsTranslation.textProvince = translate[0].labels.textProvince;
    });
  }
}
